import React, { useState, useEffect, useMemo } from 'react';
import { FaSyncAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import currentDomain from '../../../Utils/Utils';
import AddCheckModal from '../../../Components/Modals/AddCheckModal';

const money = (dollars) => `$${Number(dollars || 0).toFixed(2)}`;

const carList = (nums) => (nums && nums.length > 0 ? nums.join(', ') : '—');

// date_received comes back as an ISO timestamp; render the calendar date
// without letting the browser shift it into the previous day.
const showDate = (value) => (value ? String(value).slice(0, 10) : '—');

function ManagePayments({ judge_uuid }) {
  const [checks, setChecks] = useState([]);
  const [cards, setCards] = useState([]);
  const [unpaidCount, setUnpaidCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  // Payment token currently being refunded, so its row's button can be
  // disabled — a second click would issue a second refund.
  const [voidingCard, setVoidingCard] = useState(null);

  const fetchPayments = async () => {
    setIsLoading(true);
    try {
      const [checksRes, cardsRes, unpaidRes] = await Promise.all([
        fetch(`${currentDomain(location)}/api/payments/checks`),
        fetch(`${currentDomain(location)}/api/payments/cards`),
        fetch(`${currentDomain(location)}/api/payments/unpaid`),
      ]);
      if (!checksRes.ok || !cardsRes.ok || !unpaidRes.ok) throw new Error('Request failed');
      setChecks(await checksRes.json());
      setCards(await cardsRes.json());
      setUnpaidCount((await unpaidRes.json()).length);
    } catch (error) {
      toast.error('Failed to fetch payments.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  const handleRefresh = () => fetchPayments();

  /*
  Unlike voiding a check — which only edits our own records — this refunds the
  customer through Square and cannot be undone from here. Hence the typed
  confirmation rather than a plain OK/Cancel, and the disabled button while it
  is in flight: a double-click would attempt a second refund.
  */
  const handleVoidCard = async (card) => {
    const payer = `${card.payer_first_name ?? ''} ${card.payer_last_name ?? ''}`.trim() || 'unknown payer';
    const typed = window.prompt(
      `REFUND ${money(card.amount)} to ${payer} (card ending ${card.last4 ?? '????'})?\n\n` +
      `This refunds the customer through Square and cannot be undone here.\n` +
      `Car(s) ${carList(card.car_numbers)} will go back to unpaid.\n\n` +
      `Type REFUND to confirm:`
    );
    if (typed !== 'REFUND') {
      if (typed !== null) toast.info('Refund cancelled.');
      return;
    }

    setVoidingCard(card.payment_token);
    try {
      const response = await fetch(
        `${currentDomain(location)}/api/payments?paymentID=${encodeURIComponent(card.payment_token)}`,
        { method: 'DELETE' }
      );
      const raw = await response.text();
      if (!response.ok) {
        // The server distinguishes "refund never happened" from "refund went
        // through but our records did not update"; surface its text verbatim
        // so the admin knows whether clicking again is safe.
        toast.error(`Refund failed. ${raw}`, { autoClose: false });
        return;
      }
      const body = raw ? JSON.parse(raw) : {};
      toast.success(
        `Refund ${String(body.status ?? '').toLowerCase() || 'issued'}. ` +
        `${body.carsCleared} car(s) returned to unpaid.`
      );
      fetchPayments();
    } catch (error) {
      toast.error(`Refund failed. ${error?.message ?? error}`, { autoClose: false });
    } finally {
      setVoidingCard(null);
    }
  };

  const handleVoid = async (check) => {
    const payer = `${check.payer_first_name ?? ''} ${check.payer_last_name ?? ''}`.trim() || 'unknown payer';
    const confirmed = window.confirm(
      `Void the ${money(check.amount)} check from ${payer}?\n\n` +
      `Car(s) ${carList(check.car_numbers)} will go back to unpaid.`
    );
    if (!confirmed) return;

    try {
      const response = await fetch(`${currentDomain(location)}/api/payments/checks/${check.uuid}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error(await response.text());
      const body = await response.json();
      toast.success(`Check voided. ${body.carsCleared} car(s) returned to unpaid.`);
      fetchPayments();
    } catch (error) {
      toast.error(`Failed to void check. ${error?.message ?? error}`);
    }
  };

  const matches = (haystack) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return haystack.toLowerCase().includes(q);
  };

  const filteredChecks = useMemo(
    () => checks.filter((c) =>
      matches(`${c.payer_first_name} ${c.payer_last_name} ${c.payer_phone} ${carList(c.car_numbers)} ${c.entered_by_first_name} ${c.entered_by_last_name}`)
    ),
    [checks, searchQuery]
  );

  const filteredCards = useMemo(
    () => cards.filter((c) =>
      matches(`${c.payer_first_name} ${c.payer_last_name} ${c.payer_phone} ${carList(c.car_numbers)} ${c.last4} ${c.payment_token}`)
    ),
    [cards, searchQuery]
  );

  const checkTotal = checks.reduce((sum, c) => sum + Number(c.amount || 0), 0);
  const cardTotal = cards.reduce((sum, c) => sum + Number(c.amount || 0), 0);

  return (
    <div className="pb-8">
      <div className="text-center">
        <h1 className="font-bold text-2xl py-8">Manage Payments</h1>
      </div>

      <div className="flex justify-between items-center pb-4 gap-4">
        <input
          type="text"
          placeholder="Search by payer, phone, car number, or last 4..."
          className="border p-2 w-2/3"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div className="flex items-center gap-4">
          <button
            onClick={() => setIsAddOpen(true)}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 whitespace-nowrap"
          >
            + Add Check
          </button>
          <button onClick={handleRefresh} className="">
            <FaSyncAlt />
          </button>
        </div>
      </div>

      {isLoading ? (
        <p>Loading...</p>
      ) : (
        <>
          <h2 className="font-bold text-lg pb-2">Checks</h2>
          <div className="overflow-auto pr-4 pb-8">
            <table className="w-full border border-black">
              <thead>
                <tr className="bg-gray-200">
                  <th className="text-l">Date</th>
                  <th className="text-l">Payer</th>
                  <th className="text-l">Amount</th>
                  <th className="text-l">Cars</th>
                  <th className="text-l">Entered By</th>
                  <th className="text-l"></th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {filteredChecks.length > 0 ? (
                  filteredChecks.map((check) => (
                    <tr key={check.uuid}>
                      <td className="Table bg-slate-200">{showDate(check.date_received)}</td>
                      <td className="Table bg-slate-200">
                        {check.payer_first_name} {check.payer_last_name}
                      </td>
                      <td className="Table bg-slate-200">{money(check.amount)}</td>
                      <td className="Table bg-slate-200">{carList(check.car_numbers)}</td>
                      <td className="Table bg-slate-200">
                        {check.entered_by_first_name} {check.entered_by_last_name}
                      </td>
                      <td className="Table bg-slate-200">
                        <button
                          onClick={() => handleVoid(check)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Void
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      No matching checks found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <h2 className="font-bold text-lg pb-2">Card Payments</h2>
          <div className="overflow-auto pr-4">
            <table className="w-full border border-black">
              <thead>
                <tr className="bg-gray-200">
                  <th className="text-l">Payer</th>
                  <th className="text-l">Amount</th>
                  <th className="text-l">Last 4</th>
                  <th className="text-l">Cars</th>
                  <th className="text-l">Square Payment</th>
                  <th className="text-l"></th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {filteredCards.length > 0 ? (
                  filteredCards.map((card) => (
                    <tr key={card.payment_token}>
                      <td className="Table bg-slate-200">
                        {card.payer_first_name} {card.payer_last_name}
                      </td>
                      <td className="Table bg-slate-200">{money(card.amount)}</td>
                      <td className="Table bg-slate-200">{card.last4 ?? '—'}</td>
                      <td className="Table bg-slate-200">{carList(card.car_numbers)}</td>
                      <td className="Table bg-slate-200 font-mono text-xs truncate">
                        {card.payment_token}
                      </td>
                      <td className="Table bg-slate-200">
                        <button
                          onClick={() => handleVoidCard(card)}
                          disabled={voidingCard === card.payment_token}
                          className="text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {voidingCard === card.payment_token ? 'Refunding...' : 'Void & Refund'}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      No matching card payments found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <p className="text-sm text-gray-600 pt-4">
            Checks {money(checkTotal)} · Cards {money(cardTotal)} · {unpaidCount} car
            {unpaidCount === 1 ? '' : 's'} unpaid
          </p>
          <p className="text-xs text-gray-500 pt-1">
            Cars paid at the Square POS kiosk, in cash, or comped are marked paid from the
            entry itself and do not appear here.
          </p>
        </>
      )}

      <AddCheckModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onSaved={fetchPayments}
        judge_uuid={judge_uuid}
      />
    </div>
  );
}

export default ManagePayments;
